import { parseTelemetryConfig, type TelemetryConfigInput } from "./config.js";
import { schemaError } from "./validate.js";
import type { TelemetryRuntimeInput } from "./runtime.js";

export type TelemetryFrameworkAdapter =
  | "react"
  | "react-router"
  | "vue2"
  | "vue3"
  | "nuxt"
  | "angular"
  | "generic";

export type TelemetryInjectionScope = "singleton" | "request";

export interface TelemetryInjection {
  clientKey: string;
  serverKey: string;
  scope: TelemetryInjectionScope;
}

export interface TelemetryDiConfig {
  framework: TelemetryFrameworkAdapter;
  config?: ReturnType<typeof parseTelemetryConfig>;
  client?: Partial<TelemetryConfigInput>;
  server?: Partial<TelemetryConfigInput>;
  runtime?: TelemetryRuntimeInput;
  ssr: boolean;
  injection: TelemetryInjection;
}

export type TelemetryDiInput = {
  framework?: TelemetryFrameworkAdapter;
  config?: TelemetryConfigInput;
  client?: Partial<TelemetryConfigInput>;
  server?: Partial<TelemetryConfigInput>;
  runtime?: TelemetryRuntimeInput;
  ssr?: boolean;
  injection?: Partial<TelemetryInjection>;
};

const FRAMEWORKS = new Set<TelemetryFrameworkAdapter>([
  "react",
  "react-router",
  "vue2",
  "vue3",
  "nuxt",
  "angular",
  "generic",
]);

const INJECTION_SCOPES = new Set<TelemetryInjectionScope>(["singleton", "request"]);

const DEFAULT_INJECTION: TelemetryInjection = {
  clientKey: "telemetry.client",
  serverKey: "telemetry.server",
  scope: "singleton",
};

function parseInjection(input: Partial<TelemetryInjection> | undefined): TelemetryInjection {
  if (!input) return { ...DEFAULT_INJECTION };

  const scope = input.scope ?? DEFAULT_INJECTION.scope;
  if (!INJECTION_SCOPES.has(scope)) {
    throw schemaError('injection.scope must be "singleton" or "request"');
  }

  return {
    clientKey: input.clientKey ?? DEFAULT_INJECTION.clientKey,
    serverKey: input.serverKey ?? DEFAULT_INJECTION.serverKey,
    scope,
  };
}

function parsePartialConfig(
  input: Partial<TelemetryConfigInput> | undefined,
): Partial<TelemetryConfigInput> | undefined {
  if (!input) return undefined;
  return { ...input };
}

export function parseTelemetryDiSchema(input: TelemetryDiInput): TelemetryDiConfig {
  const framework = input.framework ?? "generic";
  if (!FRAMEWORKS.has(framework)) {
    throw schemaError("invalid framework adapter");
  }

  const config = input.config ? parseTelemetryConfig(input.config) : undefined;
  const client = parsePartialConfig(input.client);
  const server = parsePartialConfig(input.server);

  if (!config && !client && !server) {
    throw schemaError("Provide at least one of: config, client, or server");
  }

  return {
    framework,
    config,
    client,
    server,
    runtime: input.runtime,
    ssr: input.ssr ?? false,
    injection: parseInjection(input.injection),
  };
}
